import React, { useEffect, useState } from 'react';
import { Button, capitalize, Checkbox, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, Tooltip, Typography } from '@mui/material';
import EasyEdit, { Types } from 'react-easy-edit';
import moment from 'moment';

import EasyEditCustomTextbox from './EasyEditCustomTextbox';
import EasyEditCustomSelect from './EasyEditCustomSelect';
import EasyEditCustomMultiSelect from './EasyEditCustomMultiSelect';
import EasyEditCustomDatePicker from './EasyEditCustomDatePicker';

import FormGroupStatic from './FormGroupStatic';
import FormGroupStaticAmount from './FormGroupStaticAmount';
import { EasyEditCancelButton, EasyEditSaveButton } from "../../components/common/EasyEditButtons";
import EasyEditCustomAmountTextbox from './EasyEditCustomAmountTextbox';
import { useSelector } from 'react-redux';
import EasyEditDateTimePicker from './EasyEditDateTime';
import { DeleteDialog, DeleteDialogConfig, displayDate } from 'utils/Utils';
import Icon from '@mdi/react';
import { mdiClose, mdiFileDocument, mdiFilePdfBox, mdiFilePlusOutline, mdiImage, mdiMusicBox } from '@mdi/js';
import { useParams } from 'react-router-dom';
import useAxios from 'hooks/useAxios';
import { useSnackbar } from 'components/context/SnackbarContext';
import { useLoader } from 'components/context/LoaderContext';

const CustomEasyEditField = ({
    keys,
    field,
    values,
    allowEditInline,
    handleSaveData,
    onCancelEdit,
    setAllowEditInline,
    errorMessages,
    handleValidation,
    handleClick,
    backendErrorMessages,
    xsValue = 6,
    customBackendErrorMessages = [],
    setCustomBackendErrorMessages = {},
    isMultiSelect = false,
    noGrid = false
}) => {

    const { id } = useParams();
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();

    const { preferredDateFormat, preTimeZoneKey, currencyCode, currencySymbol, currencyStringValue } = useSelector((state) => state?.user);
    const fieldName = field?.field_name || '';
    const fieldLabel = field?.label || field?.field_name;
    const isRequired = field?.is_required === 'yes' ? true : false;
    const isMultiple = field?.is_multiple === 'yes' ? true : false;
    const minInput = field?.min_input || 0;
    const maxInput = field?.max_input || (field?.display_type === 'textarea' ? 1000 : 255);
    const displayType = field?.display_type;


    //Set default value from custom fields
    const matchedField = values.find(v => v.field_name === fieldName);
    const fieldValue = matchedField?.value || null;
    const [checkBoxselectedOptions, setCheckBoxselectedOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState();
    const [fieldValueState, setFieldValueState] = useState('');


    useEffect(() => {
        if (field.display_type === 'file' && typeof fieldValue === 'string') {
            setFieldValueState(fieldValue);
        }
    }, [fieldValue]);


    //for remove custom error message
    useEffect(() => {
        if (customBackendErrorMessages[fieldName]?.length > 0) {
            const timer = setTimeout(() => {
                setCustomBackendErrorMessages((prev) => {
                    const updated = { ...prev };
                    delete updated[fieldName];
                    return updated;
                });
            }, 5000); // 5 seconds

            return () => clearTimeout(timer); // Clear timeout on unmount
        }
    }, [customBackendErrorMessages, fieldName, setCustomBackendErrorMessages]);


    //Set default value from json/array custom fields
    useEffect(() => {
        if (['checkbox'].includes(field.display_type)) {
            try {
                const matchedCheckboxValues = values.find(v => v.field_name === fieldName);
                let parsedValue = matchedCheckboxValues?.value;

                // Ensure it's a proper array (not a stringified array)
                if (typeof parsedValue === 'string') {
                    parsedValue = JSON.parse(parsedValue);
                }

                if (Array.isArray(parsedValue)) {
                    setCheckBoxselectedOptions(parsedValue);
                }
            } catch (e) {
                console.error(`Invalid value for ${fieldName}`, e);
                setCheckBoxselectedOptions([]); // fallback
            }
        }

        if (['select'].includes(field.display_type) && isMultiple) {
            try {
                const matchedValues = values.find(v => v.field_name === fieldName);
                setSelectedOptions(matchedValues?.value);
            } catch (e) {
                console.error(`Invalid JSON for ${fieldName}`);
                setSelectedOptions('');
            }
        }

    }, [values, field.display_type, fieldName]);

    const handleDeleteFile = (fieldName, fileName, custom_field_id) => {
        const data = {
            model_id: parseInt(id),
            model_name: field?.model,
            custom_field_id: custom_field_id,
            field_name: fieldName,
            file_name: fileName,
        }

        try {
            DeleteDialog.fire({
                ...DeleteDialogConfig({
                    confirm: async () => {
                        showLoader()
                        await axiosInstance.delete(`api/custom-field-value/delete`, { data })
                            .then((response) => {
                                if (response.status === 200) {
                                    showSnackbar(response.data.message, "success");
                                    hideLoader();
                                    setFieldValueState(prev => prev
                                        .split(',')
                                        .filter(path => !path.includes(fileName))
                                        .join(','));
                                }
                            })
                            .then((json) => { })
                            .catch((error) => {
                                if (error.response) {
                                    hideLoader();
                                    if (error.response.status === 400) {
                                        showSnackbar(error.response.data.message, "warning");
                                    }
                                }
                            });
                    },
                    cancel: () => {
                        hideLoader();
                    }
                })
            });
        } catch (error) {
            hideLoader();
            showSnackbar('Oops Something want wrong..!', "error");
        }
    }

    const iconMapping = {
        image: mdiImage,
        audio: mdiMusicBox,
        file: mdiFileDocument,
        pdf: mdiFilePdfBox,
        // Add more mappings as needed
    };
    const [fileErrors, setFileErrors] = useState([]);
    const MAX_FILE_SIZE_MB = 4;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    let options = [];
    if (['select', 'checkbox', 'radio', 'multiselect'].includes(field.display_type)) {
        try {
            options = field.json_data_value;
        } catch (e) {
            console.error(`Invalid JSON for ${fieldName}`);
        }
    }

    const validationTypes = {
        email: 'isEmail',
        phone: 'isPhone',
        website: 'isWebsite'
    };
    const validationType = validationTypes[displayType] || '';

    switch (field.display_type) {
        case 'text':
        case 'textarea':
        case 'email':
        case 'phone':
        case 'number':
        case 'website':
            let displayComponent;

            if (field.display_type === 'email') {
                displayComponent = (
                    <FormGroupStatic
                        easyEdit
                        label={fieldLabel}
                        element={
                            fieldValue ? (
                                <a href={`mailto:${fieldValue}`} className="primary-link">
                                    {fieldValue}
                                </a>
                            ) : 'N/A'
                        }
                    />
                );
            } else if (field.display_type === 'phone') {
                displayComponent = (
                    <FormGroupStatic
                        easyEdit
                        label={fieldLabel}
                        element={
                            fieldValue ? (
                                <a href={`tel:${fieldValue}`} className="primary-link">
                                    {fieldValue}
                                </a>
                            ) : 'N/A'
                        }
                    />
                );
            } else {
                displayComponent = <FormGroupStatic easyEdit label={fieldLabel} value={'N/A'} />;
            }

            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    <EasyEdit
                        type={Types.TEXT}
                        onSave={(value) => { handleSaveData(value, fieldName) }}
                        onCancel={() => { onCancelEdit(fieldName) }}
                        value={fieldValue}
                        placeholder={fieldLabel}
                        allowEdit={allowEditInline}
                        editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label={fieldLabel} value={fieldValue} errorMessages={errorMessages[fieldName]} maxLength={maxInput} />}
                        displayComponent={displayComponent}
                        onValidate={(value) => handleValidation(value, fieldName, fieldLabel, validationType, isRequired, minInput, maxInput)} //value, fieldName, displayName, type, isrequired, min, max
                        validationMessage={errorMessages[fieldName]}
                        saveButtonLabel={<EasyEditSaveButton />}
                        cancelButtonLabel={<EasyEditCancelButton />}
                        onFocus={() => handleClick('')}
                    />
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );
        case 'date':
            return (
                <Grid item xs={noGrid ? 12 : 6} key={keys}>
                    <EasyEdit
                        type={Types.TEXT}
                        onSave={(value) => {
                            handleSaveData(value ? moment(value).format("YYYY-MM-DD") : "", fieldName);
                        }}
                        onCancel={() => { onCancelEdit(fieldName) }}
                        value={fieldValue ? moment(fieldValue).format("YYYY-MM-DD") : 'N/A'}
                        allowEdit={allowEditInline}
                        editComponent={
                            <EasyEditCustomDatePicker
                                setAllowEditInline={setAllowEditInline}
                                label={fieldLabel}
                                value={fieldValue && fieldValue !== 'N/A' ? fieldValue : null}
                                errorMessages={errorMessages[fieldName]}
                                preferredDateFormat={preferredDateFormat}
                            />}
                        displayComponent={
                            <FormGroupStaticAmount easyEdit
                                label={fieldLabel}
                                value={fieldValue}
                                preTimeZoneKey={preTimeZoneKey}
                                preferredDateFormat={preferredDateFormat}
                                displayCustomDate={true}
                            />}
                        saveButtonLabel={<EasyEditSaveButton />}
                        cancelButtonLabel={<EasyEditCancelButton />}
                        onFocus={() => handleClick('')}
                    />
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );

        case 'datetime':
            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    <EasyEdit
                        type={Types.TEXT}
                        onSave={(value) => { handleSaveData(value, fieldName) }}
                        onCancel={() => { onCancelEdit(fieldName) }}
                        format={displayDate(preferredDateFormat)}
                        value={
                            fieldValue && fieldValue !== 'N/A' ? moment.utc(fieldValue).tz(preTimeZoneKey).format(displayDate(preferredDateFormat)) : 'N/A'
                        }
                        allowEdit={allowEditInline}
                        editComponent={
                            <EasyEditDateTimePicker
                                setAllowEditInline={setAllowEditInline}
                                format={displayDate(preferredDateFormat)}
                                label={fieldLabel}
                                value={fieldValue && fieldValue !== 'N/A' ? moment.utc(fieldValue).tz(preTimeZoneKey).format(displayDate(preferredDateFormat)) : null}
                                errorMessages={errorMessages[fieldName]}
                                preferredDateFormat={preferredDateFormat}
                                isAllDay={false}
                                setParentValue={(value) => {
                                    handleSaveData(value, fieldName);
                                }}
                            />}
                        displayComponent={<FormGroupStatic
                            easyEdit
                            label={fieldLabel}
                            value={fieldValue && fieldValue !== 'N/A' ? moment.utc(fieldValue).tz(preTimeZoneKey).format(displayDate(preferredDateFormat)) : null}
                            preTimeZoneKey={preTimeZoneKey}
                            preferredDateFormat={preferredDateFormat}
                        />}
                        saveButtonLabel={<EasyEditSaveButton />}
                        cancelButtonLabel={<EasyEditCancelButton />}
                        onFocus={() => handleClick('')}
                    />
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );

        case 'select':
            if (isMultiple) {
                let selectedOption = selectedOptions; // coming from DB
                try {
                    selectedOption = JSON.parse(selectedOptions);
                } catch (e) {
                    selectedOption = ['N/A'];
                }
                return (
                    <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                        <EasyEdit
                            type={Types.SELECT}
                            onSave={(value) => {
                                let processedValue = [];

                                if (Array.isArray(value)) {
                                    processedValue = value;
                                } else if (typeof value === 'string') {
                                    processedValue = value.split(',').map(v => v.trim());
                                }

                                handleSaveData(processedValue, fieldName, 'select');
                            }}
                            onCancel={() => { onCancelEdit() }}
                            value={Array.isArray(selectedOption) ? selectedOption.join(', ') : 'N/A'}
                            allowEdit={allowEditInline}
                            editComponent={
                                <EasyEditCustomMultiSelect setAllowEditInline={setAllowEditInline} isMultiSelect={true} label={fieldLabel} fieldValue={selectedOptions} //fieldValue={selectedProductIds || []}
                                    items={options?.length > 0 && options.map(item => ({
                                        key: item.value,
                                        value: item.label
                                    }))}
                                />
                            }
                            displayComponent={<FormGroupStatic easyEdit label={fieldLabel} value={Array.isArray(selectedOption) ? selectedOption.join(', ') : 'N/A'} />} //value={selectedData?.ProductName?.join(', ')}
                            saveButtonLabel={<EasyEditSaveButton />}
                            cancelButtonLabel={<EasyEditCancelButton />}
                            onFocus={() => handleClick('')}
                        />
                        {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                    </Grid>
                );
            } else {
                return (
                    <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                        <EasyEdit
                            type={Types.SELECT}
                            onSave={(value) => { handleSaveData(value, fieldName) }}
                            onCancel={() => { onCancelEdit() }}
                            value={fieldValue ? capitalize(fieldValue) : 'N/A'}
                            allowEdit={allowEditInline}
                            editComponent={
                                <EasyEditCustomSelect focusCallback={e => { setAllowEditInline(false); }} label={fieldLabel} fieldValue={fieldValue}
                                    items={options?.length > 0 && options.map(item => ({
                                        value: item.value,
                                        option: item.label
                                    }))}
                                />
                            }
                            displayComponent={<FormGroupStatic easyEdit label={fieldLabel} value={fieldValue || 'N/A'} />}
                            saveButtonLabel={<EasyEditSaveButton />}
                            cancelButtonLabel={<EasyEditCancelButton />}
                            onFocus={() => handleClick('')}
                        />
                        {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                    </Grid>
                );
            }


        case 'amount':
            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    <EasyEdit
                        type={Types.TEXT}
                        onSave={(value) => { handleSaveData(value, fieldName) }}
                        onCancel={() => { onCancelEdit(fieldName) }}
                        value={fieldValue}
                        allowEdit={allowEditInline}
                        editComponent={<EasyEditCustomAmountTextbox setAllowEditInline={setAllowEditInline} name={fieldName} label={fieldLabel} value={fieldValue} errorMessages={errorMessages[fieldLabel]} maxLength={maxInput} currencySymbol={currencySymbol} currencyCode={currencyCode} currencyStringValue={currencyStringValue} />}
                        displayComponent={<FormGroupStaticAmount easyEdit label={fieldLabel} value={fieldValue} currencyCode={currencyCode} currencySymbol={currencySymbol} currencyStringValue={currencyStringValue} displayCustomAmount={true} />}
                        saveButtonLabel={<EasyEditSaveButton />}
                        cancelButtonLabel={<EasyEditCancelButton />}
                        onFocus={() => handleClick('')}
                    />
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );

        case 'persentage':
            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    <EasyEdit
                        type={Types.TEXT}
                        onSave={(value) => { handleSaveData(value, fieldName); setAllowEditInline(true) }}
                        onCancel={() => { onCancelEdit(fieldName) }}
                        value={fieldValue}
                        allowEdit={allowEditInline}
                        editComponent={<EasyEditCustomAmountTextbox setAllowEditInline={setAllowEditInline} name={fieldName} label={fieldLabel} value={fieldValue} errorMessages={errorMessages[fieldLabel]} />}
                        displayComponent={<FormGroupStaticAmount easyEdit label={fieldLabel} value={fieldValue} />}
                        onValidate={(value) => handleValidation(value, fieldName, fieldLabel, validationType, isRequired, '', '')} //value, fieldName, displayName, type, isrequired, min, max
                        validationMessage={errorMessages[fieldName]}
                        saveButtonLabel={<EasyEditSaveButton />}
                        cancelButtonLabel={<EasyEditCancelButton />}
                        onFocus={() => handleClick('')}
                    />
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );

        case 'multiselect':
            let selectedOption = selectedOptions; // coming from DB
            try {
                selectedOption = JSON.parse(selectedOptions);
            } catch (e) {
                selectedOption = ['N/A'];
            }
            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    <EasyEdit
                        type={Types.SELECT}
                        onSave={(value) => {
                            let processedValue = [];

                            if (Array.isArray(value)) {
                                processedValue = value;
                            } else if (typeof value === 'string') {
                                processedValue = value.split(',').map(v => v.trim());
                            }

                            handleSaveData(processedValue, fieldName, 'select');
                        }}
                        onCancel={() => { onCancelEdit() }}
                        value={Array.isArray(selectedOption) ? selectedOption.join(', ') : 'N/A'}
                        allowEdit={allowEditInline}
                        editComponent={
                            <EasyEditCustomMultiSelect setAllowEditInline={setAllowEditInline} isMultiSelect={true} label={fieldLabel} fieldValue={selectedOptions} //fieldValue={selectedProductIds || []}
                                items={options?.length > 0 && options.map(item => ({
                                    key: item.value,
                                    value: item.label
                                }))}
                            />
                        }
                        displayComponent={<FormGroupStatic easyEdit label={fieldLabel} value={Array.isArray(selectedOption) ? selectedOption.join(', ') : 'N/A'} />} //value={selectedData?.ProductName?.join(', ')}
                        saveButtonLabel={<EasyEditSaveButton />}
                        cancelButtonLabel={<EasyEditCancelButton />}
                        onFocus={() => handleClick('')}
                    />
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );

        case 'radio':
            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    {fieldLabel && <Typography variant="body-large-bold" component={'p'}>{fieldLabel}</Typography>}
                    <RadioGroup
                        aria-label={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={(e) => handleSaveData(e.target.value, fieldName)}
                        key={keys}
                        row
                    >
                        {options?.length > 0 && options.map(item => (
                            <FormControlLabel key={item?.value} value={item?.value} control={<Radio />} label={item?.label} />
                        ))}
                    </RadioGroup>
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );

        case 'checkbox':
            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    {fieldLabel && <Typography variant="body-large-bold" component={'p'}>{fieldLabel}</Typography>}
                    {options?.length > 0 &&
                        options.map((item) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkBoxselectedOptions.includes(item.value)}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;

                                            const updated = isChecked
                                                ? [...checkBoxselectedOptions, item.value]
                                                : checkBoxselectedOptions.filter(v => v !== item.value);

                                            const cleaned = Array.isArray(updated)
                                                ? updated.map(String)
                                                : [];
                                            setCheckBoxselectedOptions(cleaned);
                                            handleSaveData(cleaned, fieldName, 'checkbox');
                                        }}
                                    />
                                }
                                label={item?.label}
                                key={item?.value}
                            />
                        ))}
                    {backendErrorMessages[fieldName] && <span className="easy-edit-validation-error">{backendErrorMessages[fieldName]}</span>}
                </Grid>
            );

        case 'file':
            return (
                <Grid item xs={noGrid ? 12 : xsValue} key={keys}>
                    <div className="attachment-center">
                        <div className="title">
                            <Typography variant="title-medium-bold">{fieldLabel}</Typography> <Typography variant="title-small" className="file-size">(Max Size 4 MB)</Typography>
                        </div>

                        <div className="psuedo-upload-block">
                            <div className="content">
                                <Button color="secondary" className="btn-block" tabIndex={-1}>
                                    <span>
                                        <Icon path={mdiFilePlusOutline} color={'currentColor'} size={1} />
                                        <span>Upload File</span>
                                    </span>
                                </Button>
                            </div>
                            <input
                                type="file"
                                multiple={isMultiSelect}
                                name={fieldName}
                                className="psuedo-upload-element"
                                accept={field.accept || ".jpeg,.png,.jpg,.gif,.svg,.pdf,.mp4,.mp3,.3gp,.avi,.xls,.xlsx,.doc,.docx,.ods,.odt,.odp,.csv,.txt"}
                                onChange={(event) => {
                                    const selectedFiles = Array.from(event.currentTarget.files || []);
                                    const acceptedTypes = (field.accept || "").split(',').map(t => t.trim().toLowerCase());

                                    const validFiles = [];
                                    const newFileErrors = [];

                                    selectedFiles.forEach((file) => {
                                        const ext = file.name.split('.').pop().toLowerCase();
                                        const isAcceptedType = acceptedTypes.length === 0 || acceptedTypes.some(type => file.name.toLowerCase().endsWith(type));
                                        const isAcceptedSize = file.size <= MAX_FILE_SIZE_BYTES;

                                        if (isAcceptedType && isAcceptedSize) {
                                            validFiles.push(file);
                                        } else {
                                            const errors = [];
                                            if (!isAcceptedType) errors.push("Unsupported file type");
                                            if (!isAcceptedSize) errors.push(`File exceeds ${MAX_FILE_SIZE_MB}MB`);
                                            newFileErrors.push({ name: file.name, errors });
                                        }
                                    });

                                    setFileErrors(newFileErrors);
                                    //if no error than call api
                                    if (newFileErrors?.length === 0) {
                                        handleSaveData(validFiles, fieldName, 'file');
                                    }

                                    if (selectedFiles.length === 0) {
                                        setFileErrors([]);
                                    }
                                }}
                            //onBlur={handleBlur}
                            />

                        </div>
                    </div>

                    {fileErrors.length > 0 && (
                        <ul className="error-text" style={{ color: 'red', fontSize: '0.8rem' }}>
                            {fileErrors.map((fileErr, idx) => (
                                <li key={idx}>
                                    {fileErr.name}:{fileErr.errors.join(', ')}
                                </li>
                            ))}
                        </ul>
                    )}
                    {fieldValueState && fieldValueState !== 'N/A' && typeof fieldValueState === 'string' && fieldValueState.trim() !== '' && (
                        <Grid container component={'ul'} spacing={1.5} className="attachments">
                            {fieldValueState.split(',').map((filePath, index) => {
                                const fileName = filePath.split('/').pop();
                                const fileExtension = fileName.split('.').pop().toLowerCase();

                                let fileType = '';
                                if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) fileType = 'image';
                                else if (['mp3', 'wav', 'ogg', 'mp4'].includes(fileExtension)) fileType = 'audio';
                                else if (['pdf'].includes(fileExtension)) fileType = 'pdf';
                                else fileType = 'file';

                                const IconComponent = iconMapping[fileType];

                                return (
                                    <Grid item component={'li'} xs={12} key={index}>
                                        <a
                                            href={`${process.env.REACT_APP_API_URL}${'storage/' + filePath}`} // Replace BASE_URL appropriately
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <div className="icon-wrapper">
                                                {IconComponent && <Icon path={IconComponent} color={'currentColor'} size={1} />}
                                            </div>
                                            <div className="info">
                                                <Typography variant="body-large" component={'p'}>{fileName}</Typography>
                                            </div>
                                        </a>

                                        {/* ✅ Delete Button */}
                                        <Tooltip title="Remove">
                                            <button
                                                type="button"
                                                className="icon-button size-small"
                                                onClick={() => { handleDeleteFile(fieldName, fileName, field?.id) }}
                                            >
                                                <Icon path={mdiClose} color={'currentColor'} size={1} />
                                            </button>
                                        </Tooltip>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                    {customBackendErrorMessages[fieldName]?.length > 0 && (
                        <ul className="error-text" style={{ color: 'red', fontSize: '0.8rem' }}>
                            {customBackendErrorMessages[fieldName].map((errMsg, idx) => (
                                <li key={idx}>{errMsg}</li>
                            ))}
                        </ul>
                    )}

                </Grid>
            );


        default:
            return null;
    }
};

export default CustomEasyEditField;
